import { collection, getDocs, orderBy, query, Timestamp, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createApiCall } from '../../../services/apiClient';
import type { TriggerLogOption } from './triggerLogOptionTypes';

/**
 * Above the API's AI budget. The old callable gave up after a minute, which could abort a
 * generation that was about to write its fallback option set.
 */
const GENERATE_OPTIONS_TIMEOUT_MS = 150_000;

const generateTriggerLogOptionsCall = createApiCall<undefined, void>('generateTriggerLogOptions', {
    timeoutMs: GENERATE_OPTIONS_TIMEOUT_MS,
});

export class TriggerLogOptionService {
    private static instance: TriggerLogOptionService;

    static getInstance(): TriggerLogOptionService {
        if (!TriggerLogOptionService.instance) {
            TriggerLogOptionService.instance = new TriggerLogOptionService();
        }
        return TriggerLogOptionService.instance;
    }

    async getAllOptions(userId: string): Promise<TriggerLogOption[]> {
        const optionsRef = collection(db, `user_info/${userId}/trigger_log_options`);
        const q = query(optionsRef, orderBy('createdAt', 'asc'));

        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                category: data.category,
                emoji: data.emoji,
                shortLabel: data.shortLabel,
                longLabel: data.longLabel,
                usageCount: data.usageCount,
                lastUsedAt: data.lastUsedAt ? (data.lastUsedAt as Timestamp).toDate() : undefined,
                createdAt: (data.createdAt as Timestamp).toDate(),
                updatedAt: (data.updatedAt as Timestamp).toDate(),
            };
        });
    }

    async deleteAllOptions(userId: string): Promise<void> {
        const optionsRef = collection(db, `user_info/${userId}/trigger_log_options`);
        const querySnapshot = await getDocs(optionsRef);

        const batch = writeBatch(db);
        querySnapshot.docs.forEach((document) => {
            const docRef = doc(db, `user_info/${userId}/trigger_log_options`, document.id);
            batch.delete(docRef);
        });
        await batch.commit();
    }

    async generateTriggerLogOptions(): Promise<void> {
        await generateTriggerLogOptionsCall(undefined);
    }
}

export const triggerLogOptionService = TriggerLogOptionService.getInstance();
